import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate5';

import { type Event } from './permissions.machine.types';
import { Actions, Actors, Guards } from './setup';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    events: {} as Event,
  },
  actors: {
    getLocationPermissionStatus: Actors.getLocationPermissionStatus,
    requestLocationPermission: Actors.requestLocationPermission,
  },
  actions: {
    //
    // ─── Internal Actions ────────────────────────────────────────────────

    persistSuppressedUntil: Actions.persistSuppressedUntil,

    // ─── External Actions ────────────────────────────────────────────────

    onLocationPrimerShow: Actions.onLocationPrimerShow,
    onRequestLocationPermission: Actions.onRequestLocationPermission,
    onSkipLocationPermission: Actions.onSkipLocationPermission,
  },
  guards: {
    //
    // ─── External Guards ─────────────────────────────────────────────────

    checkIfLocationPrimerEnabled: () => false,

    // ─── Internal Guards ─────────────────────────────────────────────────

    checkIfSuppressedForever: Guards.checkIfSuppressedForever,
    checkIfSuppressionActive: Guards.checkIfSuppressionActive,
    checkIfLocationPermissionGranted: Guards.checkIfLocationPermissionGranted,
    checkIfLocationPermissionDenied: Guards.checkIfLocationPermissionDenied,
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const permissionsMachine = model.createMachine({
  id: 'permissions',
  type: 'parallel',
  states: {
    'location-permission': {
      initial: 'waiting',
      states: {
        waiting: {
          always: 'checking-if-enabled',
        },
        'checking-if-enabled': {
          always: [
            { guard: 'checkIfLocationPrimerEnabled', target: 'setup' },
            { target: 'disabled' },
          ],
        },
        setup: {
          invoke: {
            src: 'getLocationPermissionStatus',
            onDone: [
              {
                guard: {
                  type: 'checkIfLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
              },
              {
                guard: {
                  type: 'checkIfLocationPermissionDenied',
                  params: (args) => args.event.output,
                },
                target: 'denied',
              },
              { target: 'checking-if-suppressed' },
            ],
            onError: { target: 'checking-if-suppressed' },
          },
        },
        'checking-if-suppressed': {
          always: [
            { guard: 'checkIfSuppressedForever', target: 'already-shown' },
            { guard: 'checkIfSuppressionActive', target: 'already-shown' },
            { target: 'showing-primer' },
          ],
        },
        'showing-primer': {
          entry: ['onLocationPrimerShow'],
          on: {
            REQUEST_LOCATION_PERMISSIONS: {
              target: 'requesting-permission',
              actions: ['onRequestLocationPermission'],
            },
            SKIP_LOCATION_PERMISSIONS: {
              target: 'skipping',
              actions: ['onSkipLocationPermission'],
            },
          },
        },
        'requesting-permission': {
          invoke: {
            src: 'requestLocationPermission',
            onDone: [
              {
                guard: {
                  type: 'checkIfLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
              },
              { target: 'denied' },
            ],
            onError: { target: 'denied' },
          },
        },
        skipping: {
          entry: ['persistSuppressedUntil'],
          always: 'skipped',
        },
        approved: { type: 'final' },
        denied: { type: 'final' },
        'already-shown': { type: 'final' },
        skipped: { type: 'final' },
        disabled: { type: 'final' },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type PermissionsMachineActorRef = ActorRefFrom<typeof permissionsMachine>;
export type PermissionsMachineImplementations = MachineImplementationsFrom<typeof permissionsMachine>; // prettier-ignore
